import i18n from '../i18n';
import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { get, uniqueId } from 'lodash';
import Toast from './common/toast';
import { getEnvRootPath } from './common/util';

const defaultSessionValue = {
  state: "LOADING",
  partners: []
};

export const SessionContext = createContext(defaultSessionValue);

export function SessionProvider({ children }) {
  const [initialSession, setInitialSession] = useState(defaultSessionValue);
  const [session, setSession] = useState(defaultSessionValue);
  const [toast, setToast] = useState(false);

  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true
    });
  };

  async function fetchPartners() {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_PATH}/partners?page=0&pageSize=10`);
      const data = get(response, 'data.content', []);
      if (data.length > 0) {
        setSession({ ...initialSession, partners: data });
      }
      else {
        setSession({ ...initialSession, partners: [] });
      }
    } catch (e) {
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching Partner details. Please try again.');
      setSession({ ...initialSession, partners: [] });
      handleResponse(errorMessage);
    }
  }

  useEffect(() => {
    if (initialSession.state !== "LOADING" && (initialSession.roles?.includes('clientSelfServiceProjectUser') || initialSession.roles?.includes('euClientSelfServiceProjectUser'))) {
      fetchPartners();
    }
    else {
      setSession({ ...initialSession, partners: [] });
    }
  }, [initialSession])


  useEffect(() => {
    async function fetchSession() {
      try {
        const result = await axios(
          `${import.meta.env.VITE_API_PATH}/session`, { headers: { 'Cache-Control': 'no-cache' } }
        );

        setInitialSession(result.data);
        i18n.changeLanguage(result.data.locale || 'en');
        window.SynergConfig.setLocale(result.data.locale || 'en');

        if (result.data.pendoVisitorId && result.data.pendoAccountId) {
          window.pendo.initialize({
            visitor: {
              id: result.data.pendoVisitorId
            },
            account: {
              id: result.data.pendoAccountId
            }
          });
        }
      } catch (e) {
        console.log("window.location.pathname: ", window.location.pathname);
        const rootPathUri = getEnvRootPath();
        if (
          window.location.pathname !==
          `${rootPathUri}/access-denied` &&
          window.location.pathname !==
          `${rootPathUri}/login` &&
          window.location.pathname !==
          `${rootPathUri}/error`
        ) {
          window.location.href = import.meta.env.VITE_API_PATH.indexOf('https') === 0
            ? `${import.meta.env.VITE_API_PATH}/authenticate`
            : `https://${import.meta.env.VITE_DOMAIN + import.meta.env.VITE_API_PATH}/authenticate`;
        } else {
          setInitialSession({ roles: [] });
        }
      }
    }

    fetchSession();
  }, []);

  const updateLocale = (locale) => {
    i18n.changeLanguage(locale);
    setInitialSession((prev) => ({ ...prev, locale }));
    setSession((prev) => ({ ...prev, locale }));
    window.SynergConfig.setLocale(locale);
  };

  return (<>
    {toast && <Toast
      key={toast.key}
      status={toast.status}
      message={toast.message}
      showToast={toast.showToast}
    />}
    <SessionContext.Provider value={{ ...session, updateLocale }}>
      {children}
    </SessionContext.Provider>
  </>
  );
}

export function withPermissionsAndRoles(Component) {
  return function WrapperComponent(props) {
    return (
      <SessionContext.Consumer>
        {(session) => (
          <Component
            {...props}
            permissions={session.permissions}
            roles={session.roles}
          />
        )}
      </SessionContext.Consumer>
    );
  };
}

export function withSession(Component) {
  return function WrapperComponent(props) {
    return (
      <SessionContext.Consumer>
        {(session) => <Component {...props} session={session} />}
      </SessionContext.Consumer>
    );
  };
}
